import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppBar } from "@/components/AppBar";
import { Banner } from "@/components/Banner";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { Icon } from "@/components/Icon";
import { api } from "@/lib/api";
import { ZapirApiError, toApiError } from "@/lib/errors";
import { useConnection, useSession } from "@/lib/store";

const MAX_ATTEMPTS = 10;
const LOCK_MS = 15 * 60 * 1000;

/**
 * Login (RF-UX-011). Erro com copy canonica da taxonomia (§3.6), bloqueio por
 * tentativas (10/15min) e estado offline. Sem enumeracao de e-mail: a resposta
 * de credencial e sempre generica.
 */
export default function LoginScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const setLogin = useSession((s) => s.setLogin);
  const setTenant = useSession((s) => s.setTenant);
  const conn = useConnection((s) => s.conn);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ZapirApiError | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!lockedUntil) return;
    timerRef.current = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timerRef.current);
  }, [lockedUntil]);

  const locked = lockedUntil !== null && lockedUntil > now;
  const lockSeconds = locked && lockedUntil ? Math.ceil((lockedUntil - now) / 1000) : 0;
  const lockedByRate = error?.code === "RATE_LIMITED";

  const submit = async () => {
    if (locked || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.login({ email, password });
      setLogin(result.accessToken, result.me);
      const [first] = result.me.tenants;
      if (result.me.tenants.length >= 1 && first) setTenant(first.id);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(result.me.tenants.length > 1 ? "/entrar/empresa" : (from ?? "/conversas"), { replace: true });
    } catch (err) {
      const apiError = toApiError(err);
      setError(apiError);
      const nextAttempts = attempts + 1;
      setAttempts(nextAttempts);
      if (apiError.code === "RATE_LIMITED" || nextAttempts >= MAX_ATTEMPTS) {
        setLockedUntil(Date.now() + LOCK_MS);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <AppBar flat left={<span className="brandmark">Z</span>} title="" />
      <main className="forms mx-auto w-full max-w-[440px] flex-1">
        <div className="flex flex-col items-center gap-[10px] text-center">
          <h1 className="m-0 text-[22px]">Entrar no Zapir</h1>
          <p className="m-0 text-[13px] muted">Inbox de atendimento omnichannel da sua empresa.</p>
        </div>

        {conn === "offline" ? (
          <Banner tone="warn">
            <span className="dot offline" aria-hidden="true" />
            <div>Você está sem conexão. Conecte-se para entrar.</div>
          </Banner>
        ) : null}

        {error ? (
          <Banner tone={locked ? "warn" : "danger"}>
            <Icon name={locked ? "warning" : "close"} size={16} className="mt-[2px] shrink-0" />
            <div className="flex flex-col gap-1">
              <span className="font-bold">{error.copy}</span>
              <span className="text-[12px]">{error.action}</span>
              <span className="mono text-[10px] opacity-80">
                {error.status} · {error.code}
              </span>
              {locked ? (
                <span className="text-[11px]">
                  Após {MAX_ATTEMPTS} tentativas em 15 minutos o acesso fica bloqueado temporariamente.
                  {lockSeconds > 0 ? ` Tente de novo em ${Math.floor(lockSeconds / 60)}:${String(lockSeconds % 60).padStart(2, "0")}.` : ""}
                </span>
              ) : null}
            </div>
          </Banner>
        ) : null}

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <Input
            label="E-mail"
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            error={error?.status === 401 ? "Confira este e-mail." : undefined}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
          <Input
            label="Senha"
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            error={error?.status === 401 ? "Confira a senha." : undefined}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
          <Button variant="primary" block type="submit" disabled={submitting || locked || conn === "offline"}>
            {submitting ? "Entrando…" : locked ? `Bloqueado (${lockSeconds}s)` : "Entrar"}
          </Button>
          <Button variant="ghost" block onClick={() => (window.location.href = "mailto:suporte@zapir.example")}>
            Esqueci a senha
          </Button>
        </form>
      </main>
      <footer className="mx-auto w-full max-w-[440px] px-6 pb-7">
        <p className="text-center text-[11px] muted leading-5">
          Não tem conta? Peça um convite ao administrador da sua empresa.
          {lockedByRate ? " Se você não reconhece as tentativas, redefina a senha." : ""}
        </p>
      </footer>
    </div>
  );
}
